import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import { usePostHog } from 'posthog-js/react';

//internal import
import Layout from "@layout/Layout";
import useFilter from "@hooks/useFilter";
import Card from "@component/cta-card/Card";
import ProductServices from "@services/ProductServices";
import ProductCard from "@component/product/ProductCard";
import CategoryCarousel from "@component/carousel/CategoryCarousel";
import { Pagination, TableFooter } from "@windmill/react-ui";
import { AiOutlineArrowUp, AiOutlineClose } from "react-icons/ai";
import { FiFilter } from "react-icons/fi";

const Search = ({ checkSearch }) => {
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({});
  const [debouncedCheckSearch, setDebouncedCheckSearch] = useState(checkSearch);
  const [selectedFacets, setSelectedFacets] = useState({
    parent: [],
    tag: [],
    price: [],
    avgRating: [],
    ispreOrderAllowed: [],
    quantity: []
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchTime, setSearchTime] = useState(0);
  const lastSearchQuery = useRef(''); // Track last search query to avoid duplicate events
  const posthog = usePostHog();

  const {
    productData,
    setSortedField,
    handleChangePage,
    dataTable,
    sortedField,
    resultsPerPage,
    setResultPerPage,
  } = useFilter(products, 20); // Pass 20 as default resultsPerPage

  // Add price range options
  const priceRanges = [
    { label: "Under 1000", min: 0, max: 1000 },
    { label: "1000 to 2000", min: 1000, max: 2000 },
    { label: "2000 to 4000", min: 2000, max: 4000 },
    { label: "4000 to 8000", min: 4000, max: 8000 },
    { label: "Over 8000", min: 8000, max: 100000 }
  ];

  // Rating options
  const ratingOptions = [
    { label: "4 Stars & Up", value: 4 },
    { label: "3 Stars & Up", value: 3 },
    { label: "2 Stars & Up", value: 2 },
    { label: "1 Star & Up", value: 1 }
  ];

  let unmount = true;
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedCheckSearch(checkSearch);
    }, 300);

    return () => clearTimeout(timerId);
  }, [checkSearch]);

  // Track search events when debouncedCheckSearch changes
  useEffect(() => {
    if (debouncedCheckSearch && debouncedCheckSearch.trim() &&
      debouncedCheckSearch !== lastSearchQuery.current) {

      lastSearchQuery.current = debouncedCheckSearch;
      // Track search with PostHog
      posthog?.capture('Products Searched', {
        query: debouncedCheckSearch,
        source: 'search_component'
      });
    }
  }, [debouncedCheckSearch, posthog]);

  useEffect(() => {
    if (unmount) {
      // Build the search parameters including facets with proper fallback values
      const baseParams = {
        q: debouncedCheckSearch || '*',
        query_by: 'title,description,tag',
        sort_by: getSortByValue(sortedField) || 'quantity:desc,ispreOrderAllowed:desc',
        per_page: resultsPerPage || 20,
        page: dataTable?.page || 1,
        collection: 'product',
      };

      // Create the base filter string from selected facets
      let baseFilterBy = buildFilterString(selectedFacets);
      // If baseFilterBy is empty, use the default filter
      if (!baseFilterBy) {
        baseFilterBy = 'ispreOrderAllowed:true || quantity:>0';
      }

      // Create search requests
      const searches = [
        // Main search for results
        {
          ...baseParams,
          facet_by: 'parent,tag,price,avgRating,ispreOrderAllowed,quantity',
          filter_by: baseFilterBy,
          max_facet_values: 50,
          per_page: 250,  // Request 250 items (adjust if you have more products)
          page: 1  // Always request page 1, we'll handle pagination client-side
        }
      ];

      // Add separate searches for each facet type with all other filters applied
      const facetTypes = ['parent', 'tag', 'price', 'avgRating', 'ispreOrderAllowed', 'quantity'];

      facetTypes.forEach(facetType => {
        // Remove this facet type from filter if it exists
        const facetFilterString = buildFilterString({
          ...selectedFacets,
          [facetType]: [] // Remove this facet's filters for accurate counts
        });

        searches.push({
          ...baseParams,
          facet_by: facetType,
          filter_by: facetFilterString,
          max_facet_values: 50,
          per_page: 0 // We don't need hits for these facet-only queries
        });
      });

      ProductServices.searchProductWithMultiFacets({ searches })
        .then((data) => {
          if (data.results && data.results.length > 0) {
            // First result contains the actual hits
            setProducts(data.results[0].hits.map(hit => {
              return {
                ...hit.document,
                _id: hit.document.id || hit.document._id,
                highlights: hit.highlights || [] // Store the highlights properly
              };
            }));

            // Set search time from the first result
            setSearchTime(data.results[0].search_time_ms || 0);

            // Process facets from results
            const processedFacets = {};

            // Start from index 1 to skip the main search result
            for (let i = 1; i < data.results.length; i++) {
              const facetType = facetTypes[i - 1];
              if (data.results[i].facet_counts) {
                // Find the facet_counts entry for this facet type
                const facetCountEntry = data.results[i].facet_counts.find(fc => fc.field_name === facetType);
                if (facetCountEntry && facetCountEntry.counts) {
                  processedFacets[facetType] = facetCountEntry.counts;
                }
              }
            }

            setFacets(processedFacets);
          }
        })
        .catch(err => console.error("Search error:", err));
    }

    return () => {
      unmount = false;
    };
  }, [debouncedCheckSearch, sortedField, selectedFacets, resultsPerPage, dataTable?.page]); // Added dataTable?.page as dependency

  // Convert sortedField to Typesense sort_by parameter
  const getSortByValue = (sortOption) => {
    switch (sortOption) {
      case 'Low':
        return 'price:asc';
      case 'High':
        return 'price:desc';
      case 'Review':
        return 'avgRating:desc';
      case 'totalSalesCount':
        return 'totalSalesCount:desc';
      case 'New_Release':
        return 'createdAt:desc';
      default:
        return 'quantity:desc,ispreOrderAllowed:desc';
    }
  };

  // Build filter string from selected facets
  const buildFilterString = (facets) => {
    const filterParts = [];

    // Add parent category filters
    if (facets.parent && facets.parent.length > 0) {
      const parentFilter = facets.parent.map(p => `parent:=${p}`).join(' || ');
      filterParts.push(`(${parentFilter})`);
    }

    // Add tag filters
    if (facets.tag && facets.tag.length > 0) {
      const tagFilter = facets.tag.map(t => `tag:=${t}`).join(' || ');
      filterParts.push(`(${tagFilter})`);
    }

    // Add price range filters
    if (facets.price && facets.price.length > 0) {
      const priceFilters = facets.price.map(range => {
        const [min, max] = range.split('-').map(Number);
        return `(price:>=${min} && price:<${max})`;
      }).join(' || ');
      filterParts.push(`(${priceFilters})`);
    }

    // Add rating filters
    if (facets.avgRating && facets.avgRating.length > 0) {
      const ratingFilters = facets.avgRating.map(rating => {
        return `avgRating:>=${rating}`;
      }).join(' || ');
      filterParts.push(`(${ratingFilters})`);
    }

    // Add availability filters
    if (facets.ispreOrderAllowed && facets.ispreOrderAllowed.length > 0) {
      const availFilters = facets.ispreOrderAllowed.map(val => {
        return `ispreOrderAllowed:=${val}`;
      }).join(' || ');
      filterParts.push(`(${availFilters})`);
    }

    // Add quantity filter for available items
    if (facets.quantity && facets.quantity.includes('available')) {
      filterParts.push('(quantity:>0)');
    }

    return filterParts.length > 0 ? filterParts.join(' && ') : '';
  };

  // Handle facet selection
  const handleFacetChange = (facetType, value, isChecked) => {
    setSelectedFacets(prev => {
      const updatedFacets = { ...prev };

      if (isChecked) {
        // Add the value to the selected facets
        if (!updatedFacets[facetType].includes(value)) {
          updatedFacets[facetType] = [...updatedFacets[facetType], value];
        }
      } else {
        // Remove the value from selected facets
        updatedFacets[facetType] = updatedFacets[facetType].filter(item => item !== value);
      }

      return updatedFacets;
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedFacets({
      parent: [],
      tag: [],
      price: [],
      avgRating: [],
      ispreOrderAllowed: [],
      quantity: []
    });
  };

  // Get facet count for a specific value
  const getFacetCount = (facetType, value) => {
    if (!facets || !facets[facetType]) return 0;
    const facetItem = facets[facetType].find(f => f.value === value);
    return facetItem ? facetItem.count : 0;
  };

  // Process array facets (tags, parent categories)
  const processArrayFacets = (facetData) => {
    if (!facetData || facetData.length === 0) return [];
    return facetData.map(item => ({
      value: item.value,
      count: item.count
    })).sort((a, b) => b.count - a.count);
  };

  // Render standard text facets (categories, tags)
  const renderStandardFacets = (title, facetType) => {
    // Use the facet data directly from the API response
    const facetData = facets[facetType] || [];

    if (facetData.length === 0) {
      return null;
    }

    return (
      <div className="border-b border-gray-200 py-4">
        <h3 className="text-md font-serif font-medium mb-3 text-gray-700">{title}</h3>
        <div className="space-y-2">
          {facetData.slice(0, 100).map((item, idx) => {
            const isSelected = selectedFacets[facetType].includes(item.value);

            return (
              <div key={`${facetType}-${idx}`} className="flex items-center">
                <input
                  id={`${facetType}-${idx}`}
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => handleFacetChange(facetType, item.value, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                />
                <label htmlFor={`${facetType}-${idx}`} className="ml-3 text-sm text-gray-600 font-serif">
                  {item.value} <span className="text-gray-400">({item.count})</span>
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render price range facets
  const renderPriceRangeFacets = (title) => {
    return (
      <div className="border-b border-gray-200 py-4">
        <h3 className="text-md font-serif font-medium mb-3 text-gray-700">{title}</h3>
        <div className="space-y-2">
          {priceRanges.map((range, idx) => {
            const value = `${range.min}-${range.max}`;
            const isSelected = selectedFacets.price.includes(value);

            // Get counts from the facet API response
            let count = 0;
            if (facets && facets.price) {
              // Sum up counts for price values that fall within this range
              count = facets.price.reduce((sum, item) => {
                const price = parseFloat(item.value);
                if (price >= range.min && price < range.max) {
                  return sum + item.count;
                }
                return sum;
              }, 0);
            }

            // Skip rendering this price range if count is zero and it's not selected
            if (count === 0 && !isSelected) {
              return null;
            }

            return (
              <div key={`price-${idx}`} className="flex items-center">
                <input
                  id={`price-${idx}`}
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => handleFacetChange('price', value, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                />
                <label htmlFor={`price-${idx}`} className="ml-3 text-sm text-gray-600 font-serif">
                  {range.label} <span className="text-gray-400">({count})</span>
                </label>
              </div>
            );
          }).filter(Boolean)} {/* Filter out null elements */}
        </div>
      </div>
    );
  };

  // Render rating facets
  const renderRatingFacets = (title) => {
    // Check if there are any ratings that have counts > 0 or are already selected
    const hasValidRatingOptions = ratingOptions.some(option => {
      const value = option.value.toString();
      const isSelected = selectedFacets.avgRating.includes(value);

      // Calculate count for this rating option
      let count = 0;
      if (facets && facets.avgRating) {
        count = facets.avgRating.reduce((sum, item) => {
          const rating = parseFloat(item.value);
          if (rating >= option.value) {
            return sum + item.count;
          }
          return sum;
        }, 0);
      }

      return count > 0 || isSelected;
    });

    // If there are no valid rating options, don't render the section at all
    if (!hasValidRatingOptions) {
      return null;
    }

    return (
      <div className="border-b border-gray-200 py-4">
        <h3 className="text-md font-serif font-medium mb-3 text-gray-700">{title}</h3>
        <div className="space-y-2">
          {ratingOptions.map((option, idx) => {
            const value = option.value.toString();
            const isSelected = selectedFacets.avgRating.includes(value);

            // Get count from facets
            let count = 0;
            if (facets && facets.avgRating) {
              // Calculate products with rating >= option.value
              count = facets.avgRating.reduce((sum, item) => {
                const rating = parseFloat(item.value);
                if (rating >= option.value) {
                  return sum + item.count;
                }
                return sum;
              }, 0);
            }

            // Skip rendering this rating option if count is zero and it's not selected
            if (count === 0 && !isSelected) {
              return null;
            }

            return (
              <div key={`rating-${idx}`} className="flex items-center">
                <input
                  id={`rating-${idx}`}
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => handleFacetChange('avgRating', value, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                />
                <label htmlFor={`rating-${idx}`} className="ml-3 text-sm text-gray-600 font-serif">
                  {option.label} <span className="text-gray-400">({count})</span>
                </label>
              </div>
            );
          }).filter(Boolean)}
        </div>
      </div>
    );
  };

  // Render the appropriate facet group based on type
  const renderFacetGroup = (title, facetType) => {
    switch (facetType) {
      case 'parent':
      case 'tag':
        return renderStandardFacets(title, facetType);
      case 'price':
        return renderPriceRangeFacets(title);
      case 'avgRating':
        return renderRatingFacets(title);
      default:
        return null;
    }
  };

  return <>
    <div className="mx-auto max-w-screen-2xl px-3 sm:px-10">
      <div className="flex py-10 lg:py-12">
        <div className="flex w-full">
          {/* Mobile filter toggle */}
          <button
            type="button"
            className="fixed bottom-24 right-4 z-40 md:hidden rounded-full bg-gray-700 p-4 text-white shadow-lg"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
          >
            <FiFilter className="h-5 w-5" />
          </button>

          {/* Mobile filters with backdrop overlay */}
          <>
            {/* Semi-transparent overlay that closes the sidebar when clicked */}
            {showMobileFilters && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
                onClick={() => setShowMobileFilters(false)}
                aria-hidden="true"
              ></div>
            )}

            {/* Mobile filter sidebar */}
            <div
              className={`fixed inset-y-0 left-0 max-w-xs w-full shadow-lg z-40 transform bg-white p-5 transition-transform duration-300 ease-in-out md:hidden ${showMobileFilters ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
              <div className="flex items-center justify-between mb-4 border-b pb-3">
                <h2 className="text-lg font-medium font-serif text-gray-800">Filters</h2>
                <button
                  type="button"
                  className="text-gray-500"
                  onClick={() => setShowMobileFilters(false)}
                >
                  <AiOutlineClose className="h-5 w-5" />
                </button>
              </div>
              <div className="overflow-y-auto h-[calc(100vh-100px)]">
                <div className="mb-4 flex flex-wrap gap-2">
                  <button
                    onClick={clearAllFilters}
                    className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded-full font-serif"
                  >
                    Clear all filters
                  </button>
                </div>

                {/* Availability - first */}
                <div className="border-b border-gray-200 py-4">
                  <h3 className="text-md font-serif font-medium mb-3 text-gray-700">Availability</h3>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="availability-quantity"
                        type="checkbox"
                        checked={selectedFacets.quantity ? selectedFacets.quantity.includes('available') : false}
                        onChange={(e) => handleFacetChange('quantity', 'available', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                      />
                      <label htmlFor="availability-quantity" className="ml-3 text-sm text-gray-600 font-serif">
                        In-Stock
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="availability-instock"
                        type="checkbox"
                        checked={selectedFacets.ispreOrderAllowed.includes('false')}
                        onChange={(e) => handleFacetChange('ispreOrderAllowed', 'false', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                      />
                      <label htmlFor="availability-instock" className="ml-3 text-sm text-gray-600 font-serif">
                        Discontinued
                      </label>
                    </div>
                  </div>
                </div>

                {/* Price - second */}
                {renderFacetGroup('Price', 'price')}

                {/* Categories - third */}
                {renderFacetGroup('Categories', 'parent')}

                {/* Tags - fourth */}
                {renderFacetGroup('Tags', 'tag')}

                {/* Rating - fifth */}
                {renderFacetGroup('Rating', 'avgRating')}
              </div>
            </div>
          </>

          {/* Desktop sidebar with filters */}
          <div className="hidden md:block w-1/4 lg:w-1/5 pr-5">
            <div className="top-20 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="mb-4">
                <h2 className="text-lg font-medium font-serif text-gray-800 border-b border-gray-200 pb-2">Filters</h2>

                {selectedFacets.parent.length > 0 || selectedFacets.tag.length > 0 ||
                  selectedFacets.price.length > 0 || selectedFacets.avgRating.length > 0 ||
                  selectedFacets.ispreOrderAllowed.length > 0 ||
                  (selectedFacets.quantity && selectedFacets.quantity.length > 0) ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      onClick={clearAllFilters}
                      className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded-full font-serif"
                    >
                      Clear all filters
                    </button>
                  </div>
                ) : null}
              </div>

              {/* Availability - first */}
              <div className="border-b border-gray-200 py-4">
                <h3 className="text-md font-serif font-medium mb-3 text-gray-700">Availability</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="availability-quantity-desktop"
                      type="checkbox"
                      checked={selectedFacets.quantity ? selectedFacets.quantity.includes('available') : false}
                      onChange={(e) => handleFacetChange('quantity', 'available', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                    />
                    <label htmlFor="availability-quantity-desktop" className="ml-3 text-sm text-gray-600 font-serif">
                      In-Stock
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="availability-instock-desktop"
                      type="checkbox"
                      checked={selectedFacets.ispreOrderAllowed.includes('false')}
                      onChange={(e) => handleFacetChange('ispreOrderAllowed', 'false', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-white"
                    />
                    <label htmlFor="availability-instock-desktop" className="ml-3 text-sm text-gray-600 font-serif">
                      Discontinued
                    </label>
                  </div>

                </div>
              </div>

              {/* Price - second */}
              {renderFacetGroup('Price', 'price')}

              {/* Categories - third */}
              {renderFacetGroup('Categories', 'parent')}

              {/* Tags - fourth */}
              {renderFacetGroup('Tags', 'tag')}

              {/* Rating - fifth */}
              {renderFacetGroup('Rating', 'avgRating')}
            </div>
          </div>

          {/* Main content */}
          <div className="w-full md:w-3/4 lg:w-4/5">
            {/* Always show the header with search stats */}
            <div className="flex justify-between mb-3 bg-gray-700 border border-gray-100 rounded p-5">
              <h6 className="text-sm text-gray-50 font-serif" title={`Total ${productData?.length} items Found in ${searchTime} ms`}>
                {productData?.length > 0 ? (
                  <>
                    <span className="font-bold">{productData?.length}</span>{" "}
                    items Found (<span className="font-bold">{searchTime}</span> ms)
                  </>
                ) : (
                  <>
                    No products found
                  </>
                )}
              </h6>
              <div className="flex">
                <span className="text-sm font-serif mr-2 hidden md:block ">
                  <select
                    onChange={(e) => {
                      // Convert to number and default to 20 if not a valid number
                      const value = parseInt(e.target.value) || 20;
                      setResultPerPage(value);

                      // Reset to page 1 when changing results per page
                      if (dataTable?.page !== 1) {
                        handleChangePage(1);
                      }
                    }}
                    className="py-0 text-sm font-serif font-medium block w-full rounded border-0 bg-white pr-10 cursor-pointer focus:ring-0"
                    disabled={productData?.length === 0}
                  >
                    <option className="px-3" value="All" defaultValue hidden>
                      Show
                    </option>
                    <option className="px-3" value="20">
                      Default
                    </option>
                    <option className="px-3" value="20">
                      20
                    </option>
                    <option className="px-3" value="50">
                      50
                    </option>
                    <option className="px-3" value="75">
                      75
                    </option>
                    <option className="px-3" value="100">
                      100
                    </option>
                  </select>
                </span>
                <span className="text-sm font-serif">
                  <select
                    onChange={(e) => setSortedField(e.target.value)}
                    className="py-0 text-sm font-serif font-medium block w-full rounded border-0 bg-white pr-10 cursor-pointer focus:ring-0"
                    disabled={productData?.length === 0}
                  >
                    <option
                      className="px-3"
                      value="All"
                      defaultValue
                      hidden
                    >
                      Sort by
                    </option>
                    <option className="px-3" value="Default">
                      Default
                    </option>
                    <option className="px-3" value="New_Release">
                      New Release
                    </option>
                    <option className="px-3" value="Low">
                      Price: Low to High
                    </option>
                    <option className="px-3" value="High">
                      Price: High to Low
                    </option>
                    <option className="px-3" value="Review">
                      Review
                    </option>
                    <option className="px-3" value="totalSalesCount">
                      Popularity
                    </option>
                    <option className="px-3" value="Availability">
                      Availability
                    </option>
                  </select>
                </span>
              </div>
            </div>

            {/* Active filters display - only show if there are products AND filters selected */}
            {productData?.length > 0 && (selectedFacets.parent.length > 0 ||
              selectedFacets.tag.length > 0 ||
              selectedFacets.price.length > 0 ||
              selectedFacets.avgRating.length > 0 ||
              selectedFacets.ispreOrderAllowed.length > 0 ||
              (selectedFacets.quantity && selectedFacets.quantity.length > 0)) && (
                <div className="flex flex-wrap gap-2 mb-4 items-center">
                  {/* Active filters content remains the same */}
                  <span className="text-sm font-serif text-gray-600">Active filters:</span>

                  {selectedFacets.parent.map((category, idx) => (
                    <span key={`cat-${idx}`} className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full flex items-center font-serif">
                      {category}
                      <button
                        onClick={() => handleFacetChange('parent', category, false)}
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        <AiOutlineClose className="h-3 w-3" />
                      </button>
                    </span>
                  ))}

                  {selectedFacets.tag.map((tag, idx) => (
                    <span key={`tag-${idx}`} className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full flex items-center font-serif">
                      {tag}
                      <button
                        onClick={() => handleFacetChange('tag', tag, false)}
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        <AiOutlineClose className="h-3 w-3" />
                      </button>
                    </span>
                  ))}

                  {selectedFacets.price.map((priceRange, idx) => {
                    const [min, max] = priceRange.split('-');
                    const label = max === '10000' ? `Over ${min}` : `${min} to ${max}`;
                    return (
                      <span key={`price-${idx}`} className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full flex items-center font-serif">
                        {label}
                        <button
                          onClick={() => handleFacetChange('price', priceRange, false)}
                          className="ml-1 text-gray-500 hover:text-gray-700"
                        >
                          <AiOutlineClose className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}

                  {selectedFacets.avgRating.map((rating, idx) => (
                    <span key={`rating-${idx}`} className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full flex items-center font-serif">
                      {rating}★ & Up
                      <button
                        onClick={() => handleFacetChange('avgRating', rating, false)}
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        <AiOutlineClose className="h-3 w-3" />
                      </button>
                    </span>
                  ))}

                  {selectedFacets.ispreOrderAllowed.map((available, idx) => (
                    <span key={`avail-${idx}`} className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full flex items-center font-serif">
                      {available === 'true' ? 'Pre-Order' : 'In Stock'}
                      <button
                        onClick={() => handleFacetChange('ispreOrderAllowed', available, false)}
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        <AiOutlineClose className="h-3 w-3" />
                      </button>
                    </span>
                  ))}

                  {selectedFacets.quantity && selectedFacets.quantity.includes('available') && (
                    <span key="quantity-available" className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full flex items-center font-serif">
                      Available Items Only
                      <button
                        onClick={() => handleFacetChange('quantity', 'available', false)}
                        className="ml-1 text-gray-500 hover:text-gray-700"
                      >
                        <AiOutlineClose className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}

            {/* Show no results message or product grid */}
            {productData?.length === 0 ? (
              <div className="py-8 px-4 bg-white rounded border border-gray-200 shadow-sm">
                <div className="flex flex-col items-center">
                  <div className="my-4 flex justify-center">
                    <Image
                      src="/no-result.svg"
                      alt="no-result"
                      width={200}
                      height={200}
                      placeholder="blur"
                      blurDataURL="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                      style={{
                        maxWidth: "100%",
                        height: "auto"
                      }}
                    />
                  </div>
                  <h3 className="text-lg font-medium font-serif text-gray-800 mb-1">
                    No products found
                  </h3>
                  <p className="text-gray-600 text-center font-serif">
                    Try adjusting your search or filter criteria to find what you're looking for.
                  </p>
                  {(selectedFacets.parent.length > 0 || selectedFacets.tag.length > 0 ||
                    selectedFacets.price.length > 0 || selectedFacets.avgRating.length > 0 ||
                    selectedFacets.ispreOrderAllowed.length > 0 ||
                    (selectedFacets.quantity && selectedFacets.quantity.length > 0)) && (
                      <button
                        onClick={clearAllFilters}
                        className="mt-4 px-4 py-2 bg-gray-700 text-white rounded font-serif hover:bg-gray-800"
                      >
                        Clear all filters
                      </button>
                    )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 md:gap-3 lg:gap-3">
                {dataTable?.map((product, i) => (
                  <ProductCard
                    key={product._id || i}
                    product={product}
                    query={checkSearch}
                  />
                ))}
              </div>
            )}

            {/* Pagination - only show if there are products */}
            {productData?.length > 0 && (
              <TableFooter className="bg-gray-700 my-3 rounded-md">
                <Pagination
                  totalResults={products.length}
                  resultsPerPage={resultsPerPage}
                  onChange={handleChangePage}
                  label="Table navigation"
                />
              </TableFooter>
            )}

            {/* Always show the promotions and category carousel regardless of search results */}
            <div className="w-full grid grid-col gap-4 grid-cols-1 2xl:gap-6 xl:grid-cols-3 lg:grid-cols-3 md:grid-cols-2 mt-8">
              <Card />
            </div>
            <div className="relative">
              <CategoryCarousel />
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
};

export default Search;
